/**
 * PayrollBatchesPage — Issue #371.
 *
 * 工资批次列表 + 整本工资总表 .xlsx 直接上传。所有 API 调用通过
 * @jinzhuan/api-client；不维护任何前端假状态。
 *
 * Phase 1 默认入口：直接上传整本 `金专集团工资总表_模板.xlsx`，由后端解析
 * sheets / 员工行 / 项目明细。JSON payload 入口保留给自动化 / E2E / 内部脚本，
 * 这里隐藏在“高级模式”里，避免普通财务用户被 JSON 字段拉走注意力。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  usePayrollBatches,
  usePayrollBatchActions,
  type ImportPayrollWorkbookDto,
} from '@/hooks/useFinancePayroll';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  parsing: '解析中',
  parsed: '已解析',
  pending_hr_review: '待 HR 审核',
  pending_finance_review: '待财务复核',
  approved: '已批准',
  payout_in_progress: '出纳付款中',
  paid: '已发放',
  rejected: '已驳回',
  cancelled: '已撤回',
  failed: '解析失败',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  parsing: 'bg-amber-100 text-amber-800',
  parsed: 'bg-sky-100 text-sky-700',
  pending_hr_review: 'bg-blue-100 text-blue-700',
  pending_finance_review: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-violet-100 text-violet-700',
  payout_in_progress: 'bg-purple-100 text-purple-800',
  paid: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
  failed: 'bg-rose-100 text-rose-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

export default function PayrollBatchesPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];
  const canImport = hasPermission(granted, FINANCE_PERMISSIONS.PAYROLL_IMPORT);

  const { data, loading, error, fetchList } = usePayrollBatches();
  const actions = usePayrollBatchActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const now = new Date();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadYear, setUploadYear] = useState<number>(now.getFullYear());
  const [uploadMonth, setUploadMonth] = useState<number>(now.getMonth() + 1);
  const [uploadPayDate, setUploadPayDate] = useState('');
  const [uploadBatchNo, setUploadBatchNo] = useState('');
  const [uploadRemarks, setUploadRemarks] = useState('');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    void fetchList({ status: statusFilter || undefined });
  }, [fetchList, statusFilter]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const resetImportForm = () => {
    setShowImport(false);
    setUploadFile(null);
    setUploadTitle('');
    setUploadPayDate('');
    setUploadBatchNo('');
    setUploadRemarks('');
    setJsonText('');
    setImportError(null);
  };

  const handleUploadWorkbook = async () => {
    setActionMessage(null);
    setImportError(null);
    if (!uploadFile) {
      setImportError('请选择整本工资总表 .xlsx 文件');
      return;
    }
    if (!uploadTitle.trim()) {
      setImportError('请填写批次标题');
      return;
    }
    const result = await actions.uploadWorkbookFile({
      file: uploadFile,
      title: uploadTitle.trim(),
      periodYear: uploadYear,
      periodMonth: uploadMonth,
      payDate: uploadPayDate.trim() || undefined,
      batchNo: uploadBatchNo.trim() || undefined,
      remarks: uploadRemarks.trim() || undefined,
    });
    if (result) {
      setActionMessage(
        `导入完成：${result.batch.batchNo}（状态 ${STATUS_LABELS[result.batch.status] ?? result.batch.status}，错误 ${result.errors.length}）`,
      );
      resetImportForm();
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleImportJson = async () => {
    setActionMessage(null);
    setImportError(null);
    let parsed: ImportPayrollWorkbookDto;
    try {
      const value: unknown = JSON.parse(jsonText);
      if (
        typeof value !== 'object' ||
        value === null ||
        !('periodYear' in value) ||
        !('periodMonth' in value) ||
        !('title' in value) ||
        !('employees' in value) ||
        !('sheets' in value)
      ) {
        throw new Error(
          'payload 缺少 periodYear / periodMonth / title / employees / sheets',
        );
      }
      parsed = value as ImportPayrollWorkbookDto;
    } catch (e) {
      setImportError(e instanceof Error ? e.message : String(e));
      return;
    }
    const result = await actions.importWorkbook(parsed);
    if (result) {
      setActionMessage(
        `导入完成：${result.batch.batchNo}（状态 ${STATUS_LABELS[result.batch.status] ?? result.batch.status}，错误 ${result.errors.length}）`,
      );
      resetImportForm();
      await fetchList({ status: statusFilter || undefined });
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">工资总表批次</h1>
          <p className="mt-1 text-sm text-gray-500">
            按月整本导入 `金专集团工资总表_模板.xlsx`；金额、身份证、社保号默认脱敏，明文查看走 reveal 审计。
          </p>
        </div>
        {canImport && (
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700"
          >
            {showImport ? '关闭导入' : '导入整本工资总表'}
          </button>
        )}
      </div>

      {actionMessage && (
        <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
          {actionMessage}
        </div>
      )}
      {actions.actionError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          操作失败：{actions.actionError.message}
        </div>
      )}

      {showImport && (
        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {advancedMode
                ? '高级模式：粘贴整本工资总表 JSON（自动化 / E2E 场景；普通财务用户请用默认上传）。'
                : '上传整本 `金专集团工资总表_模板.xlsx`：后端解析所有 sheet，缺少必需附表会写入结构化 import_errors。'}
            </p>
            <button
              type="button"
              onClick={() => setAdvancedMode((v) => !v)}
              className="text-xs text-indigo-600 hover:underline"
            >
              {advancedMode ? '切换到上传 .xlsx' : '切换到 JSON 高级模式'}
            </button>
          </div>

          {!advancedMode && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs text-gray-500">工资总表 .xlsx</label>
                <input
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) =>
                    setUploadFile(e.target.files?.[0] ?? null)
                  }
                  className="mt-1 block w-full text-sm"
                />
                {uploadFile && (
                  <p className="mt-1 text-xs text-gray-500">
                    已选择 {uploadFile.name}（{(uploadFile.size / 1024).toFixed(1)} KiB）
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500">年份</label>
                  <input
                    type="number"
                    min={2000}
                    max={2100}
                    value={uploadYear}
                    onChange={(e) => setUploadYear(Number(e.target.value))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">月份</label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    value={uploadMonth}
                    onChange={(e) => setUploadMonth(Number(e.target.value))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">批次标题</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="2026年3月工资表"
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500">批次编号（可空）</label>
                  <input
                    type="text"
                    value={uploadBatchNo}
                    onChange={(e) => setUploadBatchNo(e.target.value)}
                    placeholder="留空自动生成"
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">发放日期（可空）</label>
                  <input
                    type="date"
                    value={uploadPayDate}
                    onChange={(e) => setUploadPayDate(e.target.value)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500">备注（可空）</label>
                <input
                  type="text"
                  value={uploadRemarks}
                  onChange={(e) => setUploadRemarks(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
            </div>
          )}

          {advancedMode && (
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder='{"periodYear": 2026, "periodMonth": 3, "title": "...", "employees": [], "sheets": []}'
              className="mt-3 h-48 w-full rounded-md border border-gray-300 p-2 font-mono text-xs"
            />
          )}

          {importError && (
            <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
              {importError}
            </div>
          )}

          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => resetImportForm()}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              取消
            </button>
            {advancedMode ? (
              <button
                type="button"
                disabled={actions.actionLoading || !jsonText.trim()}
                onClick={() => void handleImportJson()}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actions.actionLoading ? '导入中…' : '提交 JSON 导入'}
              </button>
            ) : (
              <button
                type="button"
                disabled={
                  actions.actionLoading || !uploadFile || !uploadTitle.trim()
                }
                onClick={() => void handleUploadWorkbook()}
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actions.actionLoading ? '上传中…' : '上传并解析'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">按状态过滤：</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="">全部</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          加载失败：{error.message}
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">批次编号</th>
              <th className="px-3 py-2 text-left">期间</th>
              <th className="px-3 py-2 text-left">标题</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-right">人数</th>
              <th className="px-3 py-2 text-right">错误</th>
              <th className="px-3 py-2 text-right">已发放</th>
              <th className="px-3 py-2 text-right">Hold</th>
              <th className="px-3 py-2 text-left">应发（脱敏）</th>
              <th className="px-3 py-2 text-left">实发（脱敏）</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="px-3 py-4 text-center text-gray-400" colSpan={11}>
                  加载中…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-center text-gray-400" colSpan={11}>
                  暂无工资批次
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-gray-100">
                <td className="px-3 py-2 font-mono text-xs">{row.batchNo}</td>
                <td className="px-3 py-2">
                  {row.periodYear} / {String(row.periodMonth).padStart(2, '0')}
                </td>
                <td className="px-3 py-2">{row.title}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2 text-right">{row.totalEmployeeCount}</td>
                <td className="px-3 py-2 text-right">
                  {row.errorCount > 0 ? (
                    <span className="text-rose-700">{row.errorCount}</span>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{row.paidCount}</td>
                <td className="px-3 py-2 text-right">
                  {row.heldCount > 0 ? (
                    <span className="text-amber-700">{row.heldCount}</span>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
                <td className="px-3 py-2">{row.totalGrossAmountMask}</td>
                <td className="px-3 py-2">{row.totalNetAmountMask}</td>
                <td className="px-3 py-2 text-right">
                  <Link
                    href={`/finance/payroll/${row.id}`}
                    className="text-indigo-600 hover:underline"
                  >
                    查看 →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
